/**
 * Asset Details Step
 *
 * Collects asset metadata: name, country, and per-type fields. Reads
 * and writes directly into the wizard's `stagedAssets[0].asset` slot —
 * the manual-add path's single staged asset. The bulk-XLSX path skips
 * this step entirely (asset metadata comes from the spreadsheet).
 */

import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { AssetType, type CreateAssetRequest, VehicleType } from '../../types/emissions';
import { ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';
import { TypeCardSelector } from './TypeCardSelector';
import { VEHICLE_TYPE_CARDS } from './types';

interface AssetDetailsStepProps {
  asset: CreateAssetRequest;
  onUpdate: (patch: Partial<CreateAssetRequest>) => void;
  /**
   * Skip the outer Card wrapper. Use when the step is already rendered
   * inside another container (e.g. a Modal body) so we don't double-up
   * borders and padding.
   */
  noOuterCard?: boolean;
}

const inputClass = 'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring';
const labelClass = 'tw:text-sm tw:font-medium tw:mb-1 tw:block';

const NAME_PLACEHOLDER: Partial<Record<AssetType, string>> = {
  [AssetType.Facility]: 'e.g. HQ Office Building',
  [AssetType.ManufacturingFacility]: 'e.g. Sheffield Plant',
  [AssetType.DataCentre]: 'e.g. London DC-1',
  [AssetType.Vehicle]: 'e.g. Company Car 1',
  [AssetType.Equipment]: 'e.g. Crane A',
  [AssetType.Other]: 'e.g. Custom asset',
};

// Number-field helpers: the form binds to string, the wire shape is
// number | undefined. Empty string clears the field (omitted from the
// submit payload); any other value is coerced to a Number.
const numToStr = (val?: number): string => (val !== undefined && val !== null ? String(val) : '');
const strToNum = (val: string): number | undefined => (val !== '' ? Number(val) : undefined);

export const AssetDetailsStep = ({ asset, onUpdate, noOuterCard }: AssetDetailsStepProps) => {
  const selectedType = asset.assetType;
  const brand = ASSET_TYPE_BRANDING[selectedType] ?? ASSET_TYPE_BRANDING[AssetType.Other];

  // Body shared by both wrapped and unwrapped variants. The outer Card/padding
  // is omitted when this step is already inside another container (e.g. a Modal).
  const body = (
    <>
        <div className='tw:flex tw:items-start tw:justify-between tw:mb-4 tw:gap-4'>
          <div>
            <h5 className='tw:font-semibold tw:mb-2'>Asset Details</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
              Provide details for the new asset.
            </p>
          </div>
        </div>

        <div className='tw:space-y-6'>
          {/* Asset info section */}
          <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
            {brand && (
              <div className='tw:flex tw:items-center tw:gap-2 tw:mb-4'>
                <brand.Icon className={`tw:size-4 ${brand.iconColor}`} />
                <span className='tw:text-sm tw:font-semibold'>{brand.label}</span>
                <span className='tw:text-xs tw:text-muted-foreground'>— {brand.description}</span>
              </div>
            )}
            <div className='tw:grid tw:grid-cols-3 tw:gap-4'>
              <div className='tw:col-span-2'>
                <label className={labelClass}>Name *</label>
                <input
                  type='text'
                  className={inputClass}
                  value={asset.name}
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  placeholder={NAME_PLACEHOLDER[selectedType] ?? 'Asset name'}
                />
              </div>
              <div>
                <label className={labelClass}>Unit count</label>
                <input
                  type='number'
                  className={inputClass}
                  value={numToStr(asset.unitCount)}
                  onChange={(e) => onUpdate({ unitCount: strToNum(e.target.value) })}
                  placeholder='e.g. 1'
                  min='1'
                  step='1'
                />
                <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                  Leave blank or 1 for a single unit. Set N if this entry represents
                  N similar units whose activities are reported as a combined total.
                </div>
              </div>
              <div className='tw:col-span-3'>
                <label className={labelClass}>Description</label>
                <input
                  type='text'
                  className={inputClass}
                  value={asset.description ?? ''}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  placeholder='Brief description'
                />
              </div>
            </div>
            {selectedType === AssetType.Vehicle && (
              <div className='tw:space-y-4 tw:mt-4'>
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <TypeCardSelector
                    options={VEHICLE_TYPE_CARDS}
                    value={asset.vehicleType ?? ''}
                    onChange={(value) => onUpdate({ vehicleType: (value as VehicleType) || undefined })}
                  />
                </div>
                <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                  <div>
                    <label className={labelClass}>Identifier</label>
                    <input
                      type='text'
                      className={inputClass}
                      value={asset.identifier ?? ''}
                      onChange={(e) => onUpdate({ identifier: e.target.value })}
                      placeholder='e.g. AB12 CDE or Asset #42'
                      maxLength={50}
                    />
                    <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                      Registration number, plate, or internal ID (optional)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Type-specific section */}
          {selectedType === AssetType.Facility && (
            <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
              <div className='tw:flex tw:items-center tw:gap-2 tw:mb-4'>
                <brand.Icon className={`tw:size-4 ${brand.iconColor}`} />
                <span className='tw:text-sm tw:font-semibold'>Facility Data</span>
              </div>
              <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                <div>
                  <label className={labelClass}>Total Floor Area</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.floorAreaM2)}
                    onChange={(e) => onUpdate({ floorAreaM2: strToNum(e.target.value) })}
                    placeholder='e.g. 5000'
                    min='0'
                    step='0.01'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Square meters (m²) — used for intensity: kgCO₂e/m²
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Number of Employees</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.employeeCount)}
                    onChange={(e) => onUpdate({ employeeCount: strToNum(e.target.value) })}
                    placeholder='e.g. 150'
                    min='1'
                    step='1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Used for intensity: kgCO₂e/employee
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedType === AssetType.ManufacturingFacility && (
            <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
              <div className='tw:flex tw:items-center tw:gap-2 tw:mb-4'>
                <brand.Icon className={`tw:size-4 ${brand.iconColor}`} />
                <span className='tw:text-sm tw:font-semibold'>Manufacturing Facility Data</span>
              </div>
              <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                <div>
                  <label className={labelClass}>Total Floor Area</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.floorAreaM2)}
                    onChange={(e) => onUpdate({ floorAreaM2: strToNum(e.target.value) })}
                    placeholder='e.g. 5000'
                    min='0'
                    step='0.01'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Square meters (m²) — used for intensity: kgCO₂e/m²
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Number of Employees</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.employeeCount)}
                    onChange={(e) => onUpdate({ employeeCount: strToNum(e.target.value) })}
                    placeholder='e.g. 150'
                    min='1'
                    step='1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Used for intensity: kgCO₂e/employee
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedType === AssetType.DataCentre && (
            <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
              <div className='tw:flex tw:items-center tw:gap-2 tw:mb-4'>
                <brand.Icon className={`tw:size-4 ${brand.iconColor}`} />
                <span className='tw:text-sm tw:font-semibold'>Data Centre Data</span>
              </div>
              <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                <div>
                  <label className={labelClass}>PUE</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.pue)}
                    onChange={(e) => onUpdate({ pue: strToNum(e.target.value) })}
                    placeholder='e.g. 1.4'
                    min='1'
                    max='5'
                    step='0.01'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Power Usage Effectiveness (1.0–3.0 typical)
                  </p>
                </div>
                <div>
                  <label className={labelClass}>IT Load (kW)</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.itLoadKw)}
                    onChange={(e) => onUpdate({ itLoadKw: strToNum(e.target.value) })}
                    placeholder='e.g. 500'
                    min='0'
                    step='0.1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Total IT power capacity — used for intensity: kgCO₂e/kW
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Rack Count</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.rackCount)}
                    onChange={(e) => onUpdate({ rackCount: strToNum(e.target.value) })}
                    placeholder='e.g. 200'
                    min='1'
                    step='1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Used for intensity: kgCO₂e/rack
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedType === AssetType.Equipment && (
            <div className='tw:rounded-lg tw:border tw:border-border tw:p-4'>
              <div className='tw:flex tw:items-center tw:gap-2 tw:mb-4'>
                <brand.Icon className={`tw:size-4 ${brand.iconColor}`} />
                <span className='tw:text-sm tw:font-semibold'>Equipment Data</span>
              </div>
              <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                <div>
                  <label className={labelClass}>Operating Hours / Year</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.operatingHoursPerYear)}
                    onChange={(e) => onUpdate({ operatingHoursPerYear: strToNum(e.target.value) })}
                    placeholder='e.g. 4000'
                    min='0'
                    max='8760'
                    step='1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Max 8,760 hrs/year — used for intensity: kgCO₂e/hr
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Rated Capacity (kW)</label>
                  <input
                    type='number'
                    className={inputClass}
                    value={numToStr(asset.ratedCapacityKw)}
                    onChange={(e) => onUpdate({ ratedCapacityKw: strToNum(e.target.value) })}
                    placeholder='e.g. 250'
                    min='0'
                    step='0.1'
                  />
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Used for intensity: kgCO₂e/kW
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Custom intensity — all asset types */}
          <div className='tw:rounded-lg tw:border tw:border-dashed tw:border-border tw:p-4'>
            <div className='tw:flex tw:items-center tw:gap-2 tw:mb-1'>
              <span className='tw:text-sm tw:font-semibold'>Custom Intensity Metric</span>
              <span className='tw:text-xs tw:text-muted-foreground'>(optional)</span>
            </div>
            <p className='tw:text-xs tw:text-muted-foreground tw:mb-4'>
              Define a custom denominator for intensity reporting, e.g. "Floors", "Beds", "Units Produced".
            </p>
            <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
              <div>
                <label className={labelClass}>Label</label>
                <input
                  type='text'
                  className={inputClass}
                  value={asset.customIntensityLabel ?? ''}
                  onChange={(e) => onUpdate({ customIntensityLabel: e.target.value })}
                  placeholder='e.g. Floors, Beds, Units'
                />
              </div>
              <div>
                <label className={labelClass}>Value</label>
                <input
                  type='number'
                  className={inputClass}
                  value={numToStr(asset.customIntensityValue)}
                  onChange={(e) => onUpdate({ customIntensityValue: strToNum(e.target.value) })}
                  placeholder='e.g. 12'
                  min='0'
                  step='any'
                />
                {asset.customIntensityLabel && (
                  <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                    Intensity: kgCO₂e / {asset.customIntensityLabel}
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>
    </>
  );

  if (noOuterCard) {
    return body;
  }

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>{body}</CardContent>
    </Card>
  );
};
